import React from "react";
import { Phone, MessageCircle, Upload, MapPin, Clock, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1693031630369-bd429a57f115?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwcHJpbnRpbmclMjBzaG9wJTIwcG9zdGVyJTIwYmFubmVyJTIwZmxleCUyMG1hY2hpbmV8ZW58MHx8fHwxNzg1ODYwMDM2fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { lang, tr } = useLang();
  const { content, business, waLink } = useContent();
  const kn = lang === "kn";
  const h = content.hero;

  const heroImg = h.image_id ? `${API_BASE}/content/image/${h.image_id}` : HERO_IMG;
  const stats = [
    { icon: Clock, text: kn ? tr("hero_stat_1") : h.stat1 },
    { icon: ShieldCheck, text: kn ? tr("hero_stat_2") : h.stat2 },
    { icon: MapPin, text: kn ? tr("hero_stat_3") : h.stat3 },
  ];

  return (
    <section id="top" className="relative overflow-hidden bg-slate-50 cp-grid-texture">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="cp-fade-up">
            <span className={`inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-800 ${kn ? "font-kn" : ""}`}>
              <MapPin size={13} /> {kn ? tr("hero_badge") : h.badge}
            </span>
            <h1 className={`mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] text-slate-900 ${kn ? "font-kn" : "font-heading"}`}>
              {kn ? tr("hero_title") : h.title}
            </h1>
            <p className={`mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600 ${kn ? "font-kn" : ""}`}>
              {kn ? tr("hero_sub") : h.sub}
            </p>

            <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
              <a href={`tel:${business.phone}`} data-testid="hero-call-btn">
                <Button size="lg" className="w-full sm:w-auto gap-2 bg-primary hover:bg-amber-700 text-white font-semibold shadow-lg shadow-amber-600/20">
                  <Phone size={18} /> {kn ? tr("hero_cta_call") : `Call ${business.phoneDisplay}`}
                </Button>
              </a>
              <a href={waLink("Hi Creative Prints, I want a quote for a print job.")} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn">
                <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 border-[#25D366] text-[#128C4A] hover:bg-[#25D366]/10 font-semibold">
                  <MessageCircle size={18} /> {tr("hero_cta_wa")}
                </Button>
              </a>
              <a href="#quote" data-testid="hero-quote-btn">
                <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 border-slate-300 text-slate-800 hover:bg-slate-100 font-semibold">
                  <Upload size={18} /> {tr("hero_cta_quote")}
                </Button>
              </a>
            </div>

            <div className="mt-9 grid grid-cols-3 gap-3 max-w-lg">
              {stats.map((s, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white/70 px-3 py-3">
                  <s.icon size={18} className="mt-0.5 shrink-0 text-primary" />
                  <span className={`text-xs font-semibold text-slate-700 leading-tight ${kn ? "font-kn" : ""}`}>{s.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative cp-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -inset-3 rounded-2xl bg-primary/10 rotate-2" aria-hidden />
            <img
              src={heroImg}
              alt="Creative Prints workshop signage"
              className="relative rounded-2xl border border-slate-200 shadow-2xl w-full h-[320px] sm:h-[420px] lg:h-[500px] object-cover"
            />
            <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-slate-900/85 backdrop-blur px-4 py-3 text-white">
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-300">
                <MapPin size={14} /> Vidyaranyapura Workshop
              </div>
              <p className="mt-1 text-sm font-medium leading-snug">Flex · Vinyl · Sun Board · LED Name Boards</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
